import React, { useContext, useMemo, useReducer, useState } from "react";
import { ResponsiveSankey } from "@nivo/sankey";
import { Button, Card, IconButton, Page, QueryResult, Toggle, useThemeMode, } from "@sscs/ui";
import { ArrowLeftIcon as ArrowBackIcon, ArrowRightIcon as ArrowForwardIcon, } from "@sscs/ui/icons";
import { cssVars, variables } from "@sscs/ui/styles";
import { CountBadge } from "../common";
import { ReportContext } from "../context";
import { useGetStatisticsQuery } from "../services/statistics.api";
import { getQueryStatus } from "../utils";
import { isNode, selectSubTree, transformData, } from "./dataUtils";
import styles from "./extractedFiles.module.scss";
import LinkTooltip from "./LinkTooltip";
import NodeTooltip from "./NodeTooltip";
import { extractedFilesReducer, initialState } from "./reducer";
/** Renders Extracted files chart */
const ExtractedFiles = () => {
    var _a, _b;
    const { reportId } = useContext(ReportContext);
    const { data: statistics, isLoading, isFetching, isError, error, } = useGetStatisticsQuery({ ui_id: reportId });
    const [transformation, setTransformation] = useState("READABLE");
    const { mode } = useThemeMode();
    const isDark = mode === "dark";
    const [subtreeInfo, dispatch] = useReducer(extractedFilesReducer, initialState);
    const { data: transformedData, height: initialHeight } = useMemo(() => transformData(statistics === null || statistics === void 0 ? void 0 : statistics.categories, transformation), [statistics, transformation]);
    const { data: subTree, height: estimatedHeight } = useMemo(() => subtreeInfo.current.type === "NONE"
        ? { data: transformedData, height: initialHeight }
        : selectSubTree(transformedData, subtreeInfo.current), [transformedData, subtreeInfo, initialHeight]);
    const extractedFileCount = (statistics === null || statistics === void 0 ? void 0 : statistics.statistics.extracted_count) || 0;
    return (React.createElement(Page.Root, { id: "extracted-files-by-type" },
        React.createElement(Page.Header, { title: "Extracted files by type", details: [
                `Total file${extractedFileCount !== 1 ? "s" : ""}: ${extractedFileCount.toLocaleString()}`,
                React.createElement(CountBadge, { key: "corrupted", count: (_a = statistics === null || statistics === void 0 ? void 0 : statistics.statistics.bad_checksum_count) !== null && _a !== void 0 ? _a : 0, label: "Corrupted files", severity: "none" }),
                React.createElement(CountBadge, { key: "unpacking-errors", count: (_b = statistics === null || statistics === void 0 ? void 0 : statistics.statistics.bad_format_count) !== null && _b !== void 0 ? _b : 0, label: "Unpacking errors", severity: "none" }),
            ] }),
        React.createElement(Page.Body, { className: styles.body },
            React.createElement("div", { className: styles.controls },
                React.createElement("div", { className: styles.history },
                    React.createElement(IconButton, { id: "files-button-back", disabled: subtreeInfo.past.length === 0, color: "secondary", onClick: () => dispatch({ type: "UNDO" }) },
                        React.createElement(ArrowBackIcon, null)),
                    React.createElement(IconButton, { id: "files-button-forward", disabled: subtreeInfo.future.length === 0, onClick: () => dispatch({ type: "REDO" }), color: "secondary" },
                        React.createElement(ArrowForwardIcon, null)),
                    React.createElement(Button, { id: "files-button-reset", color: "primary", onClick: () => dispatch({ type: "RESET" }) }, "Reset")),
                React.createElement(Toggle, { id: "scale-type", label: "Scale", firstValue: "Readable scale", secondValue: "Precise scale", active: transformation === "ACCURATE", onChange: (value) => {
                        if (value)
                            setTransformation("ACCURATE");
                        else
                            setTransformation("READABLE");
                    } })),
            React.createElement(QueryResult, { status: getQueryStatus({
                    isLoading,
                    isFetching,
                    isError,
                    error,
                    data: statistics,
                }) },
                React.createElement(Card.Card, null,
                    React.createElement(Card.Content, null,
                        React.createElement("div", { style: { height: `${estimatedHeight}px` } },
                            React.createElement(ResponsiveSankey, { data: subTree, margin: { top: 8, right: 180, bottom: 40, left: 90 }, align: "justify", colors: { scheme: "category10" }, nodeOpacity: 1, nodeThickness: 18, nodeInnerPadding: 3, nodeSpacing: 12, nodeBorderWidth: 0, nodeBorderColor: {
                                    from: "color",
                                    modifiers: [["darker", 0.8]],
                                }, 
                                // nivo draws links with `mix-blend-mode: multiply`, which darkens
                                // the canvas toward the link color — legible over the white
                                // light-mode canvas, but on the dark surface multiply drives every
                                // ribbon to black regardless of opacity. In dark we switch to normal
                                // compositing and raise the opacity so the colored flows show.
                                linkOpacity: isDark ? 0.6 : 0.25, linkBlendMode: isDark ? "normal" : "multiply", linkHoverOthersOpacity: 0.1, enableLinkGradient: true, labelPosition: "outside", labelOrientation: "horizontal", labelPadding: 12, 
                                // Light mode darkens the node color for readable ink on white; in
                                // dark mode that same darkening is invisible on the dark surface, so
                                // use the theme-reactive light text token instead. (nivo applies
                                // labelTextColor as an inline `fill` style, so the var() resolves.)
                                labelTextColor: isDark
                                    ? cssVars.text_primary
                                    : { from: "color", modifiers: [["darker", 3]] }, nodeTooltip: (node) => React.createElement(NodeTooltip, { node: node.node }), linkTooltip: (link) => React.createElement(LinkTooltip, { link: link.link }), theme: {
                                    labels: {
                                        text: { fontSize: variables.type_body_xs_font_size },
                                    },
                                    // nivo's tooltip container hard-codes a white background, so in
                                    // dark mode our (now light) text landed on white. Route it through
                                    // theme tokens — a raised surface + hairline border resolve
                                    // correctly in both modes (the values are var() refs).
                                    tooltip: {
                                        container: {
                                            background: cssVars.surface_raised,
                                            color: cssVars.text_primary,
                                            border: `1px solid ${cssVars.border_subtle}`,
                                        },
                                    },
                                }, onClick: (value) => {
                                    if (isNode(value))
                                        dispatch({
                                            type: "ACTION_ADDED",
                                            payload: {
                                                type: "NODE",
                                                id: value.id,
                                            },
                                        });
                                    else
                                        dispatch({
                                            type: "ACTION_ADDED",
                                            payload: {
                                                type: "LINK",
                                                source: value.source.id,
                                                target: value.target.id,
                                            },
                                        });
                                } }))))))));
};
export default ExtractedFiles;
//# sourceMappingURL=ExtractedFiles.js.map